let loaded = false;

const META_PIXEL_ID = "1497265781878140";
const GTM_ID = "GTM-58Z34ZVK";

/**
 * Caminho first-party do Google Tag Gateway (Cloudflare). Serve o mesmo
 * container do GTM a partir do nosso domínio, o que mantém os cookies
 * first-party e evita bloqueio por AdBlock / Safari ITP.
 *
 * A injeção automática do Cloudflare no <head> fica DESLIGADA no painel:
 * quem carrega é este módulo, em idle e só após consent LGPD, para não
 * competir com o bundle crítico dentro da janela do LCP.
 */
const GTM_FIRST_PARTY = `/5g6o/gtm.js?id=${GTM_ID}`;
const GTM_FALLBACK = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

/** true quando o container do GTM já foi inicializado (ex.: injeção da borda). */
function gtmAlreadyLoaded(): boolean {
  const containers = (window as unknown as { google_tag_manager?: Record<string, unknown> })
    .google_tag_manager;
  return Boolean(containers && containers[GTM_ID]);
}

/**
 * Carrega GTM e Meta Pixel sob demanda, apenas após consent LGPD = "granted".
 * Idempotente: chamadas subsequentes são ignoradas.
 *
 * O stub `window.fbq` existe em index.html apenas para enfileirar eventos
 * disparados antes desta carga real — o `init` + `PageView` acontecem aqui,
 * fora do caminho crítico do LCP (e só com consentimento).
 */
let scheduled = false;

/**
 * Agenda a carga para a primeira interação do usuário ou 5 s após `load`
 * (o que vier primeiro) — nunca dentro da janela do LCP no celular.
 */
export function loadMarketingTags(): void {
  if (scheduled || loaded || typeof window === "undefined") return;
  scheduled = true;
  const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
  let timer: number | undefined;
  const fire = () => {
    events.forEach((e) => window.removeEventListener(e, fire));
    if (timer) window.clearTimeout(timer);
    injectMarketingTags();
  };
  events.forEach((e) => window.addEventListener(e, fire, { once: true, passive: true }));
  const startTimer = () => { timer = window.setTimeout(fire, 5000); };
  if (document.readyState === "complete") startTimer();
  else window.addEventListener("load", startTimer, { once: true });
}

/**
 * Conversão aconteceu: se as tags já foram liberadas (consentimento/região)
 * mas ainda aguardam o agendamento, carrega agora para não perder o evento.
 */
export function flushMarketingTagsNow(): void {
  if (loaded || typeof window === "undefined") return;
  if (scheduled) {
    injectMarketingTags();
    return;
  }
  // Clique antes do agendamento: carrega na hora, respeitando região/consentimento.
  void import("./consent").then(({ getRegion, getConsent }) =>
    getRegion().then(({ strict }) => {
      if (getConsent() === "granted" || !strict) loadMarketingTagsNow();
    }),
  );
}

/**
 * Carga imediata (sem esperar LCP/ociosidade). Usada nas páginas /marca/*,
 * que recebem anúncio: a tag precisa estar pronta junto com os botões
 * "Ver cupom" / "Ir às compras", senão cliques rápidos não viram conversão.
 */
export function loadMarketingTagsNow(): void {
  if (loaded || typeof window === "undefined") return;
  scheduled = true;
  injectMarketingTags();
}

function injectMarketingTags(): void {
  if (loaded || typeof document === "undefined") return;
  loaded = true;

  // Google Tag Manager — via gateway first-party, com fallback para o Google.
  // Se a borda já injetou o container, não carrega de novo (evita carga dupla).
  if (!gtmAlreadyLoaded()) {
    const gtm = document.createElement("script");
    gtm.async = true;
    gtm.src = GTM_FIRST_PARTY;
    gtm.onerror = () => {
      // Gateway desligado ou caminho alterado: o rastreamento nunca cai.
      if (gtmAlreadyLoaded()) return;
      const fallback = document.createElement("script");
      fallback.async = true;
      fallback.src = GTM_FALLBACK;
      document.head.appendChild(fallback);
    };
    document.head.appendChild(gtm);
  }

  loadMetaIfGranted();
  window.addEventListener("consent-change", loadMetaIfGranted);
}

let metaLoaded = false;
function loadMetaIfGranted(): void {
  if (metaLoaded || localStorage.getItem("consent_marketing") !== "granted") return;
  metaLoaded = true;
  // Meta Pixel
  const fbq = (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq;
  if (fbq) {
    fbq("init", META_PIXEL_ID);
    fbq("track", "PageView");
  }

  const fb = document.createElement("script");
  fb.async = true;
  fb.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(fb);
}

